// src/components/Lootboxes.js
import React, {useEffect, useRef, useState} from 'react';
import 'styles/lootbox/Lootboxes.css';
import {openLootboxRequest, purchaseKeyRequest, purchaseLootboxRequest,} from 'util/api';
import {UserContext} from 'components/user/UserContext';
import {usePopup} from 'popup/PopupContext';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import LootboxRow from 'components/lootbox/LootboxRow';
import ItemIcon from 'components/items/ItemIcon';
import {useNavigate} from 'react-router-dom';
import RarityProgress from 'components/lootbox/RarityProgress';
import NiceButton from 'components/pictures/NiceButton';
import UniversalEmptyPageHeader from 'components/universal/UniversalEmptyPageHeader';

const chestImage = './images/Chest.png';
const keyImage = '/images/Key.png';

// Get the URL parameter for max lootboxes displayed
const urlParams = new URLSearchParams(window.location.search);
const MAX_LOOTBOXES_DISPLAYED = parseInt(urlParams.get('maxLootboxesDisplayed')) || 1;

// *** NEW CONSTANTS ***
const ITEM_WIDTH = 57;
const MIN_WIDTH = 320;
const MAX_PREV_ITEMS = 10;

const Lootboxes = () => {
    const [lootboxes, setLootboxes] = useState([]);
    const [keyInput, setKeyInput] = useState(1);
    const {user} = React.useContext(UserContext);
    const {openPopup} = usePopup();
    const keyInputRef = useRef(null);
    const lootboxIdRef = useRef(0);
    const [previousLootboxes, setPreviousLootboxes] = useState([]);
    const lootFoundRef = useRef(null);
    const skippedRef = useRef(false);
    const navigate = useNavigate();
    const [isKeyLoading, setIsKeyLoading] = useState(false);
    const [keyCount, setKeyCount] = useState(0);
    const [lootBoxCount, setLootBoxCount] = useState(0);
    const [visiblePrevCount, setVisiblePrevCount] = useState(MAX_PREV_ITEMS);
    const [canOpenNext, setCanOpenNext] = useState(true);
    const [wasOneOpened, setWasOneOpened] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && document.activeElement === keyInputRef.current) {
                handleGetKey();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [keyInput]);

    useEffect(() => {
        setKeyCount(Number(user.keyCount));
        setLootBoxCount(user.lootBoxCount);
    }, [user.keyCount, user.lootBoxCount]);

    // *** NEW LOGIC FOR RESPONSIVE VISIBLE PREVIOUS LOOT ***
    useEffect(() => {
        const updateVisiblePrevCount = () => {
            const width = Math.max(window.innerWidth, MIN_WIDTH);
            const items = Math.floor((width - 30) / ITEM_WIDTH);
            setVisiblePrevCount(Math.min(items, MAX_PREV_ITEMS));
        };

        updateVisiblePrevCount();
        window.addEventListener('resize', updateVisiblePrevCount);

        return () => window.removeEventListener('resize', updateVisiblePrevCount);
    }, []);

    const ensureDuration = async (asyncTask, duration = 500) => {
        const start = Date.now();
        const result = await asyncTask();
        const elapsed = Date.now() - start;
        const remaining = duration - elapsed;

        if (remaining > 0) {
            await new Promise(resolve => setTimeout(resolve, remaining));
        }

        return result;
    };

    const handleGetKey = async () => {
        const keyInputValue = Number(keyInput);
        if (!keyInputValue) {
            return;
        }

        const keyCost = 100;
        const totalCost = keyCost * keyInputValue;
        const userBalance = user.balance;

        if (userBalance < totalCost) {
            openPopup(<AddMoneyPopupWrapper initialAmount={`${totalCost - userBalance}`}/>);
            return;
        }

        try {
            setIsKeyLoading(true);
            await ensureDuration(() => purchaseKeyRequest(keyInputValue, user.balance));
            setKeyCount((prevKeyCount) => prevKeyCount + keyInputValue);
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to get key. Please try again.');
        } finally {
            setIsKeyLoading(false);
        }
    };

    const handleGetLootBox = async () => {
        try {
            setLootBoxCount((prevLootBoxCount) => prevLootBoxCount + 1);
            await purchaseLootboxRequest();
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to get loot box. Please try again.');
        }
    };

    const handleOpenLootBox = async () => {
        setWasOneOpened(true);
        setCanOpenNext(false);
        setKeyCount((prevKeyCount) => prevKeyCount - 1);
        setLootBoxCount((prevLootBoxCount) => prevLootBoxCount - 1);
        if (lootFoundRef.current && !skippedRef.current) {
            setPreviousLootboxes((prevLootboxes) => {
                const updatedLootboxes = [lootFoundRef.current, ...prevLootboxes];
                if (updatedLootboxes.length > 10) {
                    updatedLootboxes.pop();
                }
                return updatedLootboxes;
            });
        }
        skippedRef.current = false;
        let newLootbox = null;

        setLootboxes((prevLootboxes) => {
            const hasFinishedLootbox = prevLootboxes.some((lb) => lb.status === 'finished');
            const hasOpeningLootbox = prevLootboxes.some((lb) => lb.status !== 'finished');

            let updatedLootboxes = [...prevLootboxes];

            if (MAX_LOOTBOXES_DISPLAYED === 1 && hasOpeningLootbox) {
                updatedLootboxes = updatedLootboxes.filter((lb) => lb.status === 'finished');
            }

            if (hasFinishedLootbox) {
                const indexToRemove = updatedLootboxes.findIndex((lb) => lb.status === 'finished');
                updatedLootboxes.splice(indexToRemove, 1);
            }

            lootboxIdRef.current += 1;
            newLootbox = {
                id: lootboxIdRef.current,
                data: null,
                status: 'opening',
            };

            updatedLootboxes.push(newLootbox);

            return updatedLootboxes;
        });

        try {
            const itemCount = calculateItemCount();
            const data = await openLootboxRequest(itemCount);
            setLootboxes((prevLootboxes) =>
                prevLootboxes.map((lb) =>
                    lb.id === newLootbox.id ? {...lb, data: data} : lb
                )
            );
            lootFoundRef.current = data.lootFound;
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to open loot box. Please try again.');
            setLootboxes((prevLootboxes) =>
                prevLootboxes.filter((lb) => lb.id !== newLootbox.id)
            );
            setCanOpenNext(true);
        }
    };

    const calculateItemCount = () => {
        const w = window.innerWidth;
        if (w <= 320) {
            return 4;
        }

        const extra = Math.floor((w - 310) / 75);
        return Math.min(4 + extra, 7);
    };

    const handleKeyInputChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if ((value >= 1 && value <= 99) || e.target.value === '') {
            setKeyInput(value);
        }
    };

    const handleAnimationEnd = (id) => {
        setLootboxes((prevLootboxes) =>
            prevLootboxes.map((lb) =>
                lb.id === id ? {...lb, status: 'finished'} : lb
            )
        );
        setCanOpenNext(true);
    };

    function handleLootboxClick(itemType) {
        navigate(`/items-detail/${itemType}/my-items`);
    }

    return (
        <div className="lootbox-container">
            <UniversalEmptyPageHeader text={'Lootbox'} image={'lootbox'}/>
            <RarityProgress/>

            <div className="lootbox-images-container">
                <div className="lootbox-image-section">
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '18px'}}>
                        <img src={keyImage} alt="Key" className="lootbox-image"/>
                        <p>{keyCount}x</p>
                    </div>
                    <div className="key-input-container">
                        <input
                            type="number"
                            value={keyInput}
                            onChange={handleKeyInputChange}
                            min="1"
                            max="99"
                            className="key-input"
                            ref={keyInputRef}
                        />
                        <NiceButton
                            onClick={handleGetKey}
                            text={`Get${keyInput ? ` $${keyInput}` : ''}`}
                            disabled={isKeyLoading || !keyInput}
                            large={true}
                            mergeOnLeft={true}
                            minWidthForGet={true}/>
                    </div>
                </div>
                <div className="lootbox-image-section">
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start'}}>
                        <img src={chestImage} alt="Chest" className="lootbox-image" style={{paddingLeft: '25px'}}/>
                        <p>{lootBoxCount}x</p>
                    </div>
                    <div style={{paddingLeft: '18px'}}>
                        <NiceButton onClick={handleGetLootBox} text={'Purchase 0¢'} large={true}
                                    disabled={lootBoxCount >= 99}/>
                    </div>
                </div>
            </div>

            <div className="buttons-container">
                <NiceButton
                    onClick={handleOpenLootBox}
                    disabled={!keyCount || !lootBoxCount || !canOpenNext}
                    text={`Open 
                        ${wasOneOpened ? 'Next ' : ''}
                        Loot Box`}
                    large={true}
                />
            </div>

            <div className="opening-lootboxes">
                {lootboxes.map((lootbox) => (
                    <LootboxRow
                        key={lootbox.id}
                        lootboxContent={lootbox.data}
                        onRowAnimationEnd={() => handleAnimationEnd(lootbox.id)}
                        onSkippableChange={setCanOpenNext}
                    />
                ))}
            </div>
            <div className="previous-lootboxes">
                {previousLootboxes.length > 0 && (
                    <h3 style={{textAlign: 'center'}}>Previous Loot</h3>
                )}
                <div className="previous-lootboxes-row">

                    {previousLootboxes
                        .slice(0, visiblePrevCount)   // *** ONLY CHANGE IN RENDERING ***
                        .map((itemType, index) => (
                            <div
                                key={`${itemType}-${index}`}
                                className="lootbox-item finished"
                                onClick={() => handleLootboxClick(itemType)}
                            >
                                <ItemIcon lootItem={itemType} textSize={1.6}/>
                            </div>
                        ))}

                </div>
            </div>

        </div>
    );
};

export default Lootboxes;
